import { Alert, AlertTitle, Box, Typography, capitalize } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import colorConfigs from '../../../configs/colorConfigs';
import api from '../../../redux/features/auth/axiosAuthHelper';
import { RootState } from '../../../redux/store';
import RareButton from '../buttons/RareButton';
import { Audit } from '../types';
import dayjs from 'dayjs';

interface AuditDetailProps {
    audit_id?: number | null;
    handleRefreshButton: () => void;
    handleBackButton: () => void;
}

const formatValue = (value: unknown): string => {
    if (value == null) return '—';
    if (typeof value === 'string') return value;
    return JSON.stringify(value, null, 2) ?? String(value);
};

const AuditValue = ({ label, value }: { label: string; value: unknown }) => (
    <Box sx={{ minWidth: 0 }}>
        <Typography variant="caption" display="block" sx={{ color: colorConfigs.text }}>{label}</Typography>
        <Typography variant="body2" sx={{ color: colorConfigs.brand.logoText, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
            {formatValue(value)}
        </Typography>
    </Box>
);

const AuditDetailCard = ({ audit_id, handleBackButton }: AuditDetailProps) => {
    const access = useSelector((state: RootState) => state.auth.access);
    const closeRef = useRef(handleBackButton);
    closeRef.current = handleBackButton;
    const [audit, setAudit] = useState<Audit | null>(null);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!audit_id) return;
        let active = true;
        let closeTimer: ReturnType<typeof setTimeout> | undefined;
        setAudit(null);
        setError(null);
        api.get(`audit-logs/${audit_id}`, {
            headers: { Authorization: `Bearer ${access}` },
        }).then(response => {
            if (active) setAudit(response.data as Audit);
        }).catch(fetchError => {
            if (!active) return;
            console.error('Error fetching audit', fetchError);
            setError(`No Audit found with ID ${audit_id}`);
            closeTimer = setTimeout(() => closeRef.current(), 4000);
        });
        return () => {
            active = false;
            if (closeTimer) clearTimeout(closeTimer);
        };
    }, [audit_id, access]);

    const changes = Object.entries(audit?.changes ?? {});
    const isUpdate = audit?.action?.toLowerCase() === 'update';
    const isDelete = audit?.action?.toLowerCase() === 'delete';

    return (
        <Box sx={{ px: { xs: 1, sm: 2 }, pt: 1, pb: 2, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            {error && <Alert severity="error"><AlertTitle>Error</AlertTitle>{error}</Alert>}
            {audit && (
                <>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' }, gap: 2 }}>
                        <AuditValue label="Audit log ID" value={audit.id} />
                        <AuditValue label="Timestamp" value={audit.timestamp ? dayjs(audit.timestamp).format('YYYY-MM-DD HH:mm:ss') : '—'} />
                        <AuditValue label="User ID" value={audit.user_id} />
                        <AuditValue label="User email" value={audit.user_email} />
                        <AuditValue label="Table name" value={audit.table_name} />
                        <AuditValue label="Action" value={audit.action ? capitalize(audit.action) : '—'} />
                    </Box>
                    <Box sx={{ borderTop: '1px solid #e1e8e6', pt: 2 }}>
                        <Typography variant="body2" fontWeight={700} sx={{ color: colorConfigs.text, mb: 1.5 }}>Changes</Typography>
                        {changes.length === 0 ? (
                            <Typography variant="body2" sx={{ color: colorConfigs.text }}>No field changes recorded.</Typography>
                        ) : changes.map(([field, pair], index) => {
                            const [oldValue, newValue] = Array.isArray(pair) ? pair : [undefined, pair];
                            return (
                                <Box key={field} sx={{ py: 1.5, borderTop: index ? '1px solid #e1e8e6' : 0 }}>
                                    <Typography variant="body2" fontWeight={600} sx={{ color: colorConfigs.text, mb: 1 }}>{field}</Typography>
                                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: isUpdate ? 'repeat(2, minmax(0, 1fr))' : '1fr' }, gap: 2 }}>
                                        {(isUpdate || isDelete) && <AuditValue label="Old value" value={oldValue} />}
                                        {!isDelete && <AuditValue label="New value" value={newValue} />}
                                    </Box>
                                </Box>
                            );
                        })}
                    </Box>
                </>
            )}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', borderTop: '1px solid #e1e8e6', pt: 1.5 }}>
                <RareButton variant="outlined" onClick={handleBackButton}>Close</RareButton>
            </Box>
        </Box>
    );
};

export default AuditDetailCard;
